// src/utils/getFontValue.ts
import { useTheme, Breakpoint } from "@mui/material/styles";
import { TypographyStyle } from "@mui/material/styles";
import useResponsive from "../hooks/useResponsive";

// ----------------------------------------------------------------------

type TypographyVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption' | 'button' | 'overline';

export default function GetFontValue(variant: TypographyVariant) {
  const theme = useTheme();
  const breakpoint = useWidth();
  // When breakpoint is "xl", use "lg" as the responsive key; otherwise use the breakpoint directly.
  const key = theme.breakpoints.up(breakpoint === "xl" ? "lg" : breakpoint);

  // Determine if the variant has responsive values.
  const hasResponsive =
    variant === "h1" ||
    variant === "h2" ||
    variant === "h3" ||
    variant === "h4" ||
    variant === "h5" ||
    variant === "h6";

  // Get the font settings. If the variant is responsive and the theme has a breakpoint-specific value, use that.
  // Otherwise, fall back to the default typography value.
  const getFont = 
    hasResponsive && theme.typography[variant] && 
    typeof (theme.typography[variant] as any)[key] !== 'undefined'
      ? (theme.typography[variant] as any)[key]
      : theme.typography[variant];

  const fontSize = remToPx(getFont.fontSize);
  const lineHeight = Number((theme.typography[variant] as TypographyStyle).lineHeight) * fontSize;
  const { fontWeight, letterSpacing } = theme.typography[variant] as TypographyStyle;

  return { fontSize, lineHeight, fontWeight, letterSpacing };
}

// ----------------------------------------------------------------------
// Utility functions

export function remToPx(value: string): number {
  return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value: number): string {
  return `${value / 16}rem`;
}

export function responsiveFontSizes({
                                      sm,
                                      md,
                                      lg,
                                    }: {
  sm: number;
  md: number;
  lg: number;
}) {
  return {
    "@media (min-width:600px)": {
      fontSize: pxToRem(sm),
    },
    "@media (min-width:900px)": {
      fontSize: pxToRem(md),
    },
    "@media (min-width:1200px)": {
      fontSize: pxToRem(lg),
    },
  };
}

// ----------------------------------------------------------------------
// Custom hook to determine the current breakpoint using useResponsive

function useWidth(): Breakpoint {
  const theme = useTheme();
  // Ensure that the breakpoint keys are typed as Breakpoint[]
  const keys = theme.breakpoints.keys as Breakpoint[];

  // Call useResponsive for each breakpoint at the top level (complying with the Rules of Hooks)
  // Coerce undefined to false using the nullish coalescing operator (??)
  const upXs = useResponsive("up", "xs") ?? false;
  const upSm = useResponsive("up", "sm") ?? false;
  const upMd = useResponsive("up", "md") ?? false;
  const upLg = useResponsive("up", "lg") ?? false;
  const upXl = useResponsive("up", "xl") ?? false;

  // Map each breakpoint to its responsive match result
  const breakpointMatches: Record<Breakpoint, boolean> = {
    xs: upXs,
    sm: upSm,
    md: upMd,
    lg: upLg,
    xl: upXl,
  };

  // Reverse the keys (largest breakpoint first) and find the first that matches
  const reversedKeys = [...keys].reverse();
  const matchedBreakpoint = reversedKeys.find((key) => breakpointMatches[key]);

  // If no breakpoint matches, default to "xs"
  return matchedBreakpoint || "xs";
}
